import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { createPortal } from 'react-dom';

import { useClassName, useWindowSize } from 'common/hooks';

import { SVGIcon } from 'common/components';
import { Modal } from 'common/lazy';
import { CTA_TYPES, STANDARD_EVENTS } from 'utils/analytics/trackV2';
import HomepageImage from 'public/screens/Home/Components/Image';
import useCustomStyle from '../../hooks';
import { getStylesByPrefix } from '../../utils';

import './link.less';
import { jumpTo } from 'utils/helpers/scroll';

export default function Link({
  className,
  style: customStyle,
  component,
  refs,
  children,
  handleTrack,
  onMouseEnter,
  onMouseLeave,
}) {
  if (!component?.fields) return null;
  const classNames = useClassName('CLPLink');
  const [setRef, style] = useCustomStyle(component.fields.settings);
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const noLink = style && style['--no-link'] === 'true';
  const configuredJumpOffset = Number.parseInt(style['--jump-offset'], 10);
  const jumpOffset = Number.isFinite(configuredJumpOffset)
    ? configuredJumpOffset
    : -48;
  const { isMobile } = useWindowSize();
  const isDownloadLink = component.fields.type === 'download';
  const linkStyle = {
    width: style['--link-width'] || undefined,
    height: style['--link-height'] || undefined,
    ...(component.fields.backgroundColor && {
      backgroundColor: component.fields.backgroundColor,
    }),
    ...(component.fields.fontColor && { color: component.fields.fontColor }),
    ...(style?.['--link-font-size'] && {
      fontSize: style['--link-font-size'],
    }),
    ...(style?.['--link-font-weight'] && {
      fontWeight: style['--link-font-weight'],
    }),
    // Apply the link's own --link-* settings (background-color, color,
    // justify-content, margin-top, ...) as real inline styles. Without this
    // they are only set as inert --link-* CSS variables that no rule consumes.
    ...getStylesByPrefix('--link-', style),
    ...customStyle,
  };

  const useIconLeft = style['--icon-left'] === 'true';

  const getCtaType = () => {
    if (component.fields.type === 'jump') return CTA_TYPES.jump;
    if (component.fields.type === 'modal') return CTA_TYPES.modal;
    if (isDownloadLink || component.fields.isPdf) return CTA_TYPES.download;
    return CTA_TYPES.link;
  };

  const trackClick = () => {
    if (!handleTrack) return;
    const event = STANDARD_EVENTS.click;
    const params = {
      label: component.fields.label,
      cta_type: getCtaType(),
      destination_url: component.fields.url,
    };
    handleTrack({ event, params });
  };

  const handleOpenModal = (id, refs) => {
    const modalElement = refs.current.find((ref) => ref.id === id);
    if (isMobile) {
      jumpTo(id, refs, 0);
    }
    modalElement.classList.add('open');
  };

  const handleDownload = async () => {
    try {
      const url = component.fields.url;
      const filename = url.split('/').pop() || 'download-asset';
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      console.error(`Could not download the file, Error:${e.message}`);
    }
  };

  const onClick = (e) => {
    trackClick();
    if (isDownloadLink) {
      e.preventDefault();
      return handleDownload();
    } else if (noLink) {
      e.preventDefault();
      return;
    } else if (component.fields.type === 'jump' && refs) {
      e.preventDefault();
      return jumpTo(component.fields.url, refs, jumpOffset);
    } else if (component.fields.type === 'modal' && refs) {
      e.preventDefault();
      return handleOpenModal(component.fields.url, refs);
    } else if (component.fields.isPdf) {
      setIsOpen(true);
    } else {
      if (component.fields.type === 'external') {
        window.open(component.fields.url, '_blank');
      } else {
        history.push(component.fields.url);
      }
    }
    e.preventDefault();
  };

  return (
    <>
      {component.fields.isPdf && (
        <Modal
          onClose={() => setIsOpen(false)}
          className={classNames('modal')}
          open={isOpen}>
          {createPortal(
            <div
              className={classNames('close-button')}
              onClick={() => setIsOpen(false)}>
              <SVGIcon name="close" size="tiny" />
            </div>,
            document.getElementsByTagName('body')[0]
          )}
          <object
            data={component.fields.url}
            type="application/pdf"
            width="100%"
            height="100%"
            style={{
              height: '80vh',
            }}>
            <p>
              <a href={component.fields.url}>Click here to open the menu</a>
            </p>
          </object>
        </Modal>
      )}
      {children ? (
        <a
          href={component.fields.url}
          onClick={onClick}
          ref={setRef}
          className={className}
          style={linkStyle}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}>
          {children}
        </a>
      ) : (
        <a
          style={linkStyle}
          className={classNames([
            component.fields.theme !== 'other' && 'container',
            component.fields.theme,
            className,
          ])}
          href={noLink ? '' : component.fields.url}
          ref={setRef}
          onClick={onClick}
          download={
            isDownloadLink ? component.fields.url.split('/').pop() : null
          }>
          {useIconLeft && Boolean(component.fields.icon) && (
            <HomepageImage field={component.fields.icon} />
          )}
          {component.fields.label}
          {!useIconLeft && Boolean(component.fields.icon) && (
            <HomepageImage field={component.fields.icon} />
          )}
        </a>
      )}
    </>
  );
}

Link.propTypes = {
  component: PropTypes.object.isRequired,
  ref: PropTypes.object,
};

Link.defaultProps = {
  ref: null,
  children: null,
};
